import { Component, inject, Input, model, OnInit, output, signal } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { SmartlockerService } from '../../services/smartlocker.service';
import { LoadingScreenComponent, LoadingScreenModel, MaterialModel, ModalClass } from '@evorait/pace-core';
import { forkJoin, map, Observable, of, take } from 'rxjs';
import {
    SmartlockerMaterial,
    SmartLockerMaterialWithAccessCode,
} from '../../interfaces/smartlocker-deliveries.interface';
import {
    SmartlockerData,
    SmartlockerDoorData,
    SmartlockerDoorMaterials,
} from '../../interfaces/smartlocker-doors.interface';
import { LogInstanceToken } from '@evorait/pace-logging';
import { catchError, mergeMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'pace-smartlocker-pickup',
    templateUrl: './smartlocker-pickup.component.html',
    standalone: false,
})
export class SmartlockerPickupComponent implements OnInit {
    public material = model.required<MaterialModel>();
    @Input() public readonly smartlockerData: SmartlockerData;
    public pickedUpMaterials = signal<SmartLockerMaterialWithAccessCode[]>([]);
    public pickedInSmartlocker = output<void>();

    private readonly smartlockerService = inject(SmartlockerService);
    private readonly modalInstance = inject(BsModalRef);
    public groupedMaterialsByDoor$: Observable<SmartlockerDoorMaterials[]>;
    private readonly log = inject(LogInstanceToken);
    private readonly translate = inject(TranslateService);
    private readonly modalService = inject(BsModalService);

    public ngOnInit(): void {
        const materialNumber = this.material().materialIdentifier.materialNumber.value;
        const smartlockerId = this.smartlockerService.getSmartlockerIdFromStorageLocationDesc(
            this.material().storageLocationDesc.value,
        );

        this.groupedMaterialsByDoor$ = forkJoin([
            this.smartlockerService.getSmartlockerMaterials(smartlockerId).pipe(take(1)),
            this.smartlockerService.getDoorsForLocker(smartlockerId).pipe(take(1)),
        ]).pipe(
            map(([materials, smartlockerDoorsData]) => {
                const matchingMaterials = materials.filter((m) => m.MaterialNumber === materialNumber);
                return this.groupMaterialsByDoor(matchingMaterials, smartlockerDoorsData);
            }),
        );
    }

    /**
     * Groups the (already material-number filtered) materials by their SmartlockerDoorId,
     * so the template can render one section per door with its materials.
     *
     * @param materials the materials already filtered by material number
     * @param smartlockerDoorsData smartlocker doors
     * @returns an array of door groups, each holding the materials located behind that door
     */
    private groupMaterialsByDoor(
        materials: SmartlockerMaterial[],
        smartlockerDoorsData: SmartlockerDoorData[],
    ): SmartlockerDoorMaterials[] {
        const groupedMaterialsByDoor = new Map(
            materials.map((material) => [
                material.SmartlockerDoorId,
                materials.filter((mat) => material.SmartlockerDoorId === mat.SmartlockerDoorId),
            ]),
        );

        return Array.from(groupedMaterialsByDoor, ([doorId, doorMaterials]) => ({
            doorId,
            doorData: smartlockerDoorsData.find((door) => door.SmartlockerDoorId === doorId),
            materials: doorMaterials,
        }));
    }

    public close(): void {
        this.modalInstance.hide();
    }

    public smartlockerPickupCompleted(): void {
        const loadingScreenModel = new LoadingScreenModel(
            <string>this.translate.instant('SMARTLOCKER_TRANSFER_IN_PROGRESS_LABEL'),
        );
        const loadingScreenInstance = this.modalService.show(LoadingScreenComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_SMALL_NO_FULLSCREEN,
            initialState: {
                model: loadingScreenModel,
            },
            ariaLabelledBy: 'loading-screen-modal-title',
        });

        loadingScreenModel.loadingScreenDisplayed$
            .pipe(
                take(1),
                mergeMap(() =>
                    this.smartlockerService.setMaterialsStatusToTransferred(this.pickedUpMaterials()).pipe(
                        take(1),
                        catchError((error) => {
                            this.log.error(
                                `SmartlockerPickupComponent: smartlockerPickupCompleted: the material/s pickup process from Smartlocker failed: ${error}`,
                            );
                            return of(false);
                        }),
                    ),
                ),
            )
            .subscribe((materialsCollected) => {
                loadingScreenInstance.hide();
                if (materialsCollected) {
                    this.material.update((prev) => {
                        const updatedMaterial = prev;
                        updatedMaterial.quantity.value = this.pickedUpMaterials().length;
                        return updatedMaterial;
                    });

                    this.pickedInSmartlocker.emit();
                    this.close();
                }
            });
    }
}
